import { useState, useEffect } from 'react';
import { Bell, BellOff, X } from 'lucide-react';
import { Button } from './ui/button';
import { useNotifications } from '../context/NotificationContext';
import { useAuth } from '../context/AuthContext';

const NotificationPrompt = () => {
  const { user } = useAuth();
  const { permission, supported, requestPermission } = useNotifications();
  const [showPrompt, setShowPrompt] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Only show prompt if:
    // - User is logged in
    // - Notifications are supported
    // - Permission hasn't been granted or denied
    // - User hasn't dismissed the prompt in this session
    const dismissed = sessionStorage.getItem('notification_prompt_dismissed');
    
    if (user && supported && permission === 'default' && !dismissed) {
      // Delay showing the prompt
      const timer = setTimeout(() => setShowPrompt(true), 5000);
      return () => clearTimeout(timer);
    }
  }, [user, supported, permission]);

  const handleEnable = async () => {
    setLoading(true);
    await requestPermission();
    setLoading(false);
    setShowPrompt(false);
  };

  const handleDismiss = () => {
    sessionStorage.setItem('notification_prompt_dismissed', 'true');
    setShowPrompt(false);
  };

  if (!showPrompt) return null;

  return (
    <div 
      className="fixed top-4 left-4 right-4 sm:left-auto sm:right-4 sm:w-96 z-50 animate-slide-down"
      data-testid="notification-prompt"
    >
      <div className="bg-zinc-900 border border-emerald-500/30 rounded-xl shadow-2xl overflow-hidden">
        <div className="p-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
              <Bell className="w-5 h-5 text-emerald-400" />
            </div>
            
            <div className="flex-1">
              <h3 className="font-bold text-white mb-1">Enable Notifications</h3>
              <p className="text-sm text-zinc-400 mb-3">
                Get alerts when your favorite teams are about to play and receive our top picks!
              </p>
              
              <div className="flex gap-2">
                <Button
                  onClick={handleEnable}
                  disabled={loading}
                  className="bg-emerald-500 hover:bg-emerald-600 text-sm"
                  size="sm"
                >
                  {loading ? (
                    <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                  ) : (
                    <>
                      <Bell className="w-4 h-4 mr-1" />
                      Enable
                    </>
                  )}
                </Button>
                <Button
                  onClick={handleDismiss}
                  variant="ghost"
                  className="text-zinc-400 hover:text-white text-sm"
                  size="sm"
                >
                  Not now
                </Button>
              </div>
            </div>
            
            <button 
              onClick={handleDismiss}
              className="text-zinc-500 hover:text-white transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotificationPrompt;
